/**
 * Subject Master — filter roster by department, program, semester
 */

import { useMemo } from 'react';
import { programBelongsToDepartment } from './StudentMasterFilters';

export default function SubjectMasterFilters({
  subjectFilters,
  onFiltersChange,
  departments,
  programs,
  canShowList,
  listShown,
  resultCount,
  onShowList,
  listLoading,
}) {
  const departmentName = subjectFilters.department;
  const activePrograms = (programs || []).filter((p) => p.is_active !== false);
  let programOptions = activePrograms.filter((p) => programBelongsToDepartment(p, departmentName, departments));
  if (departmentName && programOptions.length === 0) {
    programOptions = activePrograms;
  }

  const selectedProgram = programOptions.find((p) => String(p.id) === String(subjectFilters.degreeId));
  const maxSem = Number(selectedProgram?.number_of_semesters || 8);
  const semesterOptions = useMemo(() => {
    const list = Array.from({ length: Math.max(1, Math.min(20, maxSem)) }, (_, i) => i + 1);
    return list;
  }, [maxSem]);

  const handleDepartmentChange = (value) => {
    onFiltersChange((f) => ({
      ...f,
      department: value,
      degreeId: '',
      semester: '',
    }));
  };

  const handleProgramChange = (value) => {
    onFiltersChange((f) => ({
      ...f,
      degreeId: value,
      semester: '',
    }));
  };

  const statusMessage = (() => {
    if (!subjectFilters.department) return 'Select a department.';
    if (!subjectFilters.degreeId) return 'Select a program, then click Show.';
    if (!listShown) {
      const semHint = subjectFilters.semester !== '' && subjectFilters.semester != null
        ? `semester ${subjectFilters.semester}`
        : 'all semesters';
      return `Ready — click Show to load subjects (${semHint}).`;
    }
    return `Showing ${resultCount ?? 0} subject${resultCount === 1 ? '' : 's'}.`;
  })();

  return (
    <div className="student-roster-panel subject-roster-panel">
      <div className="student-roster-panel-head">
        <div>
          <h3 className="student-roster-title">Filters</h3>
          <p className="student-roster-subtitle">Department, program, and optional semester</p>
        </div>
      </div>

      <div className="student-roster-filters faculty-roster-filters">
        <div className="student-roster-field">
          <label className="student-roster-label" htmlFor="subject-filter-dept">Department</label>
          <select
            id="subject-filter-dept"
            value={subjectFilters.department}
            onChange={(e) => handleDepartmentChange(e.target.value)}
            className="input student-roster-select"
          >
            <option value="">Select department</option>
            {(departments || []).map((d) => (
              <option key={d.id || d.code || d.name} value={d.name}>
                {d.code ? `${d.code} — ${d.name}` : d.name}
              </option>
            ))}
          </select>
        </div>

        <div className="student-roster-field">
          <label className="student-roster-label" htmlFor="subject-filter-program">Program</label>
          <select
            id="subject-filter-program"
            value={subjectFilters.degreeId}
            onChange={(e) => handleProgramChange(e.target.value)}
            className="input student-roster-select"
            disabled={!departmentName}
          >
            <option value="">Select program</option>
            {programOptions.map((p) => (
              <option key={p.id} value={p.id}>
                {p.program_code || p.code} — {p.name}
              </option>
            ))}
          </select>
        </div>

        <div className="student-roster-field">
          <label className="student-roster-label" htmlFor="subject-filter-sem">Semester</label>
          <select
            id="subject-filter-sem"
            value={subjectFilters.semester === '' || subjectFilters.semester == null ? '' : subjectFilters.semester}
            onChange={(e) => {
              const v = e.target.value;
              onFiltersChange((f) => ({
                ...f,
                semester: v === '' ? '' : Number(v),
              }));
            }}
            className="input student-roster-select"
            disabled={!subjectFilters.degreeId}
          >
            <option value="">Any</option>
            {semesterOptions.map((s) => (
              <option key={s} value={s}>Semester {s}</option>
            ))}
          </select>
        </div>

        <div className="student-roster-field student-roster-field-action">
          <span className="student-roster-label student-roster-label--invisible">Show</span>
          <button
            type="button"
            className="btn-primary student-roster-show-btn"
            disabled={!canShowList || listLoading}
            onClick={onShowList}
          >
            {listLoading ? 'Loading…' : 'Show'}
          </button>
        </div>
      </div>

      <p className="student-roster-status">{statusMessage}</p>
    </div>
  );
}
